import { useMemo } from "react";
import { ColorPicker } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPalette } from "@fortawesome/free-solid-svg-icons";
import classNames from "classnames";
import {
  BOOKING_PRESET_COLORS,
  buildAntdColorPresets,
  isValidHexColor,
  readableTextColor,
} from "../../utils/bookingColor";
import "./Booking.css";

type Props = {
  /** Hex `#rrggbb`, oppure vuoto per "colore automatico in base al tipo". */
  value?: string | null;
  onChange?: (color?: string) => void;
  disabled?: boolean;
  className?: string;
};

/**
 * Selettore del colore del blocco prenotazione: palette di colori suggeriti
 * cliccabili + selettore libero antd. Compatibile con `Form.Item`
 * (`value` / `onChange`).
 */
export default function BookingColorPicker({
  value,
  onChange,
  disabled,
  className,
}: Props) {
  const current = isValidHexColor(value) ? value.toLowerCase() : undefined;
  const isPreset = !!current && BOOKING_PRESET_COLORS.includes(current);

  // Copia per antd: il ColorPicker modifica in place l'array `colors` dei
  // preset, quindi non gli si passa mai la costante condivisa.
  const antdPresets = useMemo(() => buildAntdColorPresets(), []);

  return (
    <div className={classNames("bk-color-picker", className)}>
      <div className="bk-color-swatches">
        <button
          type="button"
          disabled={disabled}
          title="Colore automatico (in base al tipo)"
          className={classNames("bk-color-swatch bk-color-swatch--auto", {
            active: !current,
          })}
          onClick={() => onChange?.(undefined)}
        >
          Auto
        </button>
        {BOOKING_PRESET_COLORS.map((color) => (
          <button
            key={color}
            type="button"
            disabled={disabled}
            title={color}
            className={classNames("bk-color-swatch", {
              active: current === color,
            })}
            style={{
              background: color,
              color: readableTextColor(color),
            }}
            onClick={() => onChange?.(color)}
          >
            {current === color ? "✓" : ""}
          </button>
        ))}
        <ColorPicker
          disabled={disabled}
          value={current}
          format="hex"
          disabledAlpha
          presets={antdPresets}
          // Dentro le modali Bootstrap (z-index 1055) il pannello va montato
          // nel contenitore del trigger, altrimenti resta nascosto dietro la
          // modale e il focus-trap della modale ne blocca gli input.
          getPopupContainer={(trigger) => trigger.parentElement ?? document.body}
          onChange={(color) => onChange?.(color.toHexString())}
        >
          <button
            type="button"
            disabled={disabled}
            title="Scegli un colore personalizzato"
            className={classNames("bk-color-swatch bk-color-swatch--custom", {
              active: !!current && !isPreset,
            })}
            style={
              current && !isPreset
                ? { background: current, color: readableTextColor(current) }
                : undefined
            }
          >
            <FontAwesomeIcon icon={faPalette} />
          </button>
        </ColorPicker>
      </div>
      <small className="bk-color-hint">
        {current
          ? `Il blocco userà il colore ${current}`
          : "Senza scelta il colore dipende dal tipo di prenotazione"}
      </small>
    </div>
  );
}
